"use client";

import { useState, useEffect, useRef } from "react";
import type { TocItem } from "./legalDocument.type";

export default function TermsOfUseTOC({ toc }: { toc: TocItem[] }) {
  const [activeSection, setActiveSection] = useState<string>("");
  const activeItemRef = useRef<HTMLButtonElement>(null);
  const isInitialLoad = useRef(true);
  const scrollRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = toc.map((s) => document.getElementById(s.anchor));
      const scrollPosition = window.scrollY;

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const el = sectionElements[i];
        if (el && el.offsetTop <= scrollPosition) {
          setActiveSection(toc[i].anchor);
          break;
        }
      }
    };
    scrollRef.current = handleScroll;
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [toc]);

  // Scroll the active item into view within the TOC (but not on initial load)
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    if (activeItemRef.current) {
      activeItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeSection]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;

    window.scrollTo({
      top: el.offsetTop + 400,
      behavior: "smooth",
    });
    setTimeout(() => {
      window.addEventListener("scroll", scrollRef.current!);
    }, 1000);
  };

  return (
    <aside className="hidden lg:block w-72 shrink-0">
      <div className="sticky top-32 bg-white rounded-2xl shadow-sm border p-6 max-h-[calc(100vh-10rem)] flex flex-col">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-6">
          TABLE OF CONTENTS
        </h2>

        <div className="relative flex-1 overflow-hidden">
          <nav className="relative space-y-1 pl-4 overflow-y-auto h-full pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            {/* Vertical line — now correctly sized */}
            <div className="absolute left-0 top-0 bottom-0 w-px bg-gray-200"></div>

            {toc.map((section) => {
              const isActive = activeSection === section.anchor;

              return (
                <button
                  key={section.anchor}
                  ref={isActive ? activeItemRef : null}
                  onClick={() => {
                    setActiveSection(section.anchor);
                    window.removeEventListener("scroll", scrollRef.current!);
                    scrollToSection(section.anchor);
                  }}
                  className={`relative w-full text-left py-2 text-sm transition-all
            ${isActive ? "text-blue-700 font-medium" : "text-gray-600 hover:text-black"}
          `}
                >
                  {isActive && (
                    <span className="absolute -left-4 top-0 bottom-0 w-1 bg-blue-600 rounded-r-full" />
                  )}
                  <span>{section.title}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    </aside>
  );
}

